// © Microsoft Corporation. All rights reserved.

import { Comment, User } from "@msnews/social-data-service";
import { CommentConnector, ComponentConnector, FollowInfo, PopupConnector, UsersConnector } from "../../Social.props";
import { ProfileCommentItemTelemetryObjects, profileCommentItemTelemetryActionDispatchers, profileCommentItemTelemetryContracts } from "./ProfileCommentItem.telemetry";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";
import { arrow_reply, chevron_down, ellipsis, exclamation_round, trash } from "../../utils/icons";
import { hasThemeBase, isComponentDisabled } from "../../utils/themeHelpers";

import DeleteMenu from "../../common/delete-menu/DeleteMenu";
import DisabledMessage from "../../comment/disabled-message/DisabledMessage";
import MostReactions from "../../common/most-reactions/MostReactions";
import React from "react";
import ReportMenu from "../../common/report-menu/ReportMenu";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import UserCard from "../../comment/user-card/UserCard";
import { contentImageFallback } from "../../utils/avatarFallback";
import {enums} from "../../utils/enums";
import getAgoText from "../../utils/getAgoText";
import openArticlePage from "../../utils/openArticlePage";
import openGuidelinePage from "../../utils/openGuidelinePage";
import openProfilePage from "../../utils/openProfilePage";

export interface ProfileCommentItemProps {
    componentConnector: ComponentConnector;
    comment: Comment;
    user: User;
    t: SocialLocalizedStrings;
    me: User;
    managedClasses?: SocialClassNameContract;
    users: { [id: string]: FollowInfo;}; // dictionary definening
    usersConnector: UsersConnector;
    commentConnector: CommentConnector;
    popupConnector: PopupConnector;
    market: string;
    mainTelemetryObject?: TelemetryObjectBase;
    theme: string;
    responsiveClass: string;
}

interface ProfileCommentItemState {
    miniReactionBreakdownVisible: boolean,
    userCardVisible: boolean,
    userCardUserId: string,
    deleteMenuVisible: boolean,
    reportMenuVisible: boolean,
    selectedReportType: string,
    messageTruncated: boolean,
    isHoverUserCard: boolean,
    isHoverMiniReactionBreakdown: boolean
}

export default class ProfileCommentItem extends React.Component<ProfileCommentItemProps, ProfileCommentItemState> {
    messageRef: React.RefObject<HTMLDivElement>;
    telemetryObjects: ProfileCommentItemTelemetryObjects;
    reactionsCountBtn: HTMLElement;

    constructor(props) {
        super(props);
        this.state = {
            miniReactionBreakdownVisible: false,
            userCardVisible: false,
            userCardUserId: "",
            deleteMenuVisible: false,
            reportMenuVisible: false,
            selectedReportType: null,
            messageTruncated: null,
            isHoverUserCard: false,
            isHoverMiniReactionBreakdown: false
        };

        this.messageRef = React.createRef();
        this.reactionsCountBtn = null;
        this.telemetryObjects = null;
    }

    componentDidMount() {
        if (this.props.comment.mentionUser && !(this.props.users && this.props.users[this.props.comment.mentionUser])) {
            this.props.usersConnector.fetchUser(this.props.comment.mentionUser);
        }
        this.checkMessageTruncated();
    }

    componentDidUpdate() {
        this.checkMessageTruncated();
    }

    checkMessageTruncated() {
        /** Check if message is truncated by webkit clamp */
        if (this.messageRef && this.messageRef.current && this.state.messageTruncated == null) {
            this.messageRef.current.scrollHeight > this.messageRef.current.clientHeight + 10
                ? this.setState({messageTruncated: true})
                : this.setState({messageTruncated: false});
        }
    }

    showUserCard(e, userId, isHoverUserCard=true) {
        this.setState({userCardVisible: true, userCardUserId: userId, isHoverUserCard: isHoverUserCard});
    }

    deleteComment(e, commentId, parentId, userId) {
        this.props.commentConnector.deleteComment(commentId, parentId, userId);
    }

    reportHandler(reportType, e) {
        this.setState({
            reportMenuVisible: false,
            selectedReportType: reportType
        });
    }

    showReportMenu(e) {
        const { theme, popupConnector, comment } = this.props;
        const isMobile = this.props.responsiveClass.includes(enums.responsiveLevels.L1);
        const isEmbeded = isComponentDisabled(theme, "reportmenu");
        const isSuperApp = theme && hasThemeBase(theme, enums.theme.superappBase);

        if (isEmbeded || isMobile || isSuperApp) {
            popupConnector.openPopup("reportmenu", { id: comment.id });
        } else {
            this.setState({
                reportMenuVisible: true
            });
        }
    }

    onReactionsClick(e, id) {
        this.props.popupConnector.openPopup("reactionBreakdownComment", {id, parentTelemetryObject: this.telemetryObjects && this.telemetryObjects.reactionBreakdown, focusBackRef: this.reactionsCountBtn});
        profileCommentItemTelemetryActionDispatchers.reactionCountsClicked(e.currentTarget, this.telemetryObjects && this.telemetryObjects.reactionCountsBtn);
    }

    render() {
        const {componentConnector, comment, user, me, managedClasses, t, users, usersConnector, market, mainTelemetryObject, theme} = this.props;
        const {id, createDatetime, content, text, parentId, reactionCounts, replyCount, mentionUser, isDeleted, isInReview, visibleStatus} = comment;
        const isReply = (!parentId || 0 === parentId.length);
        const isSelf = me && me.id == user.id;
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);

        /** Generate Telemetry Objects */
        const commentItemTelemetryObject = addOrUpdateTelemetryObject(mainTelemetryObject, profileCommentItemTelemetryContracts.commentItem, { id }, this.telemetryObjects && this.telemetryObjects.commentItem);
        this.telemetryObjects = {
            commentItem: commentItemTelemetryObject,
            replyLink: addOrUpdateTelemetryObject(commentItemTelemetryObject, profileCommentItemTelemetryContracts.replyLink, { id }, this.telemetryObjects && this.telemetryObjects.replyLink),
            reactionCountsBtn: addOrUpdateTelemetryObject(commentItemTelemetryObject, profileCommentItemTelemetryContracts.reactionCountsBtn, { id }, this.telemetryObjects && this.telemetryObjects.reactionCountsBtn),
            reactionBreakdown: addOrUpdateTelemetryObject(commentItemTelemetryObject, profileCommentItemTelemetryContracts.reactionBreakdown, { id }, this.telemetryObjects && this.telemetryObjects.reactionBreakdown)
        };

        // Reaction Counts
        const totalReactionCount = reactionCounts ? Object.values(reactionCounts).reduce(($sum, $count) => $sum + $count, 0) : 0;

        /* SUB ELEMENTS */
        let commentBody; // message
        if (isDeleted) {
            commentBody = <span className="cwt-link cwt-comment-deleted-message" role="link" aria-label={t.DisabledMessagesGuidelines} {...openGuidelinePage(componentConnector, theme, market, "_blank")}>
                {t[enums.visibleStatusMap[visibleStatus] || enums.visibleStatusMap.Default]}
                <i className="cwt-icon cwt-disabled-message">{exclamation_round}</i>
            </span>;
        } else {
            commentBody =
        <React.Fragment>
            {mentionUser &&
            <React.Fragment>
                <a
                    className="cwt-name cwt-link"
                    onMouseEnter={(e) => this.showUserCard(e, mentionUser)}
                    {...openProfilePage(componentConnector, theme, mentionUser, market)}
                >{users && users[mentionUser] && users[mentionUser].data && users[mentionUser].data.name}</a>
                { !isSuperApp &&
                    <i className="cwt-link cwt-visible-on-focus" role="button" aria-label={t.AriaLabelsShowUserCard} onClick={(e) => this.showUserCard(e, mentionUser, false)}> {chevron_down} </i>
                }
                &nbsp;
            </React.Fragment>
            }
            <div dangerouslySetInnerHTML={{__html: text}} />
        </React.Fragment>;
        }
        let innerContent;
        if (this.state.selectedReportType) {
            innerContent = <DisabledMessage {...{managedClasses, t, message: t.VisibleStatusInReview, mainTelemetryObject: this.telemetryObjects.commentItem, hideIcon: true}} />;
        } else if (isInReview) {
            innerContent = <DisabledMessage {...{managedClasses, t, message: t[enums.visibleStatusMap[visibleStatus] || t.VisibleStatusInReview], hideIcon: true}} />;
        } else {
            innerContent =
            <React.Fragment>
                <div className="cwt-message-engagement-container">
                    <div className="cwt-message cwt-align-to-header">
                        <div className={this.state.messageTruncated == null || this.state.messageTruncated ? "cwt-clamp" : ""} ref={this.messageRef}>{commentBody}</div>
                        { this.state.messageTruncated && <label className="cwt-show-clamped-btn cwt-link" role="button" onClick={() => this.setState({messageTruncated: false})}>{t.CommentItemReadMore}</label>}
                    </div>
                    {this.state.userCardVisible &&
                    <UserCard
                        {...{usersConnector, users, managedClasses, me, t, id: this.state.userCardUserId, visible: true, market,
                            onHide: () => this.setState({userCardVisible: false, isHoverUserCard: false}),
                            triggerRef: this.messageRef,
                            isHover: this.state.isHoverUserCard,
                            mainTelemetryObject
                        }}
                    />
                    }
                    { (totalReactionCount > 0 || replyCount > 0) &&
                        <div className="cwt-engagements">
                            <MostReactions
                                containerClassName="cwt-reaction-counts"
                                onReactionsClick={e => this.onReactionsClick(e, comment.id)}
                                setReactionsCountRef={element => this.reactionsCountBtn = element}
                                {...{ reactionCounts, t, managedClasses, theme}}
                            />

                            { replyCount > 0 &&
                                <a className="cwt-link" {...openArticlePage(theme, content.url, content.id, comment.id, "_blank")} {...renderHybrid(this.telemetryObjects.replyLink, false)}>
                                    <i className="cwt-icon cwt-replies-link">
                                        {arrow_reply}
                                        <span className="cwt-icon-label">{
                                            replyCount == 1
                                                ? t.ProfileCommentsReplyLink.replace("{#}", "1")
                                                : t.ProfileCommentsReplyPluralLink.replace("{#}", replyCount.toString())
                                        }</span>
                                    </i>
                                </a>
                            }
                        </div>
                    }
                </div>
                <a className="cwt-link cwt-open-content-link" {...openArticlePage(theme, content.url, content.id, comment.id, "_blank")}>
                    <div className="cwt-content-info">
                        <img alt="Content thumbnail" className="cwt-content-image" src={content.thumbnail} onError={contentImageFallback} />
                        <span className="cwt-content-title">{content.title}</span>
                    </div>
                </a>
            </React.Fragment>;
        }
        return (
            <li className="cwt-profile-comment cwt-comment-highlight" key={id} {...renderHybrid(this.telemetryObjects.commentItem, true)}>
                <div className="cwt-header">
                    <span className="cwt-title">{isReply ? t.ProfileCommentItemComment : t.ProfileCommentItemCommentReply}</span>
                    <span className="cwt-posted-at dot-before">{getAgoText(createDatetime)}</span>
                    <div className="cwt-comment-config-container">
                        {!isDeleted &&
                    (isSelf ?
                        <React.Fragment>
                            <i id={`deleteMenu_${id}`} className="cwt-icon cwt-link" role="button" aria-label={t.AriaLabelsOpenDeleteCommentMenu} onClick={() => this.setState({deleteMenuVisible: true})}>
                                {trash}
                            </i>
                            {this.state.deleteMenuVisible &&
                            <DeleteMenu
                                id={id}
                                t={t}
                                visible={this.state.deleteMenuVisible}
                                onConfirmDelete={ e => this.deleteComment(e, comment.id, comment.parentId, user.id)}
                                onHide={() => this.setState({deleteMenuVisible: false})}
                                managedClasses={managedClasses}
                                className={managedClasses.profileCommentsDeleteMenuPopover}
                                mainTelemetryObject={this.telemetryObjects && this.telemetryObjects.commentItem}
                            />
                            }
                        </React.Fragment>
                        :
                        <React.Fragment>
                            <i id={`reportMenu_${id}`} className="cwt-icon cwt-link" role="button" aria-label={t.AriaLabelsOpenReportCommentMenu} onClick={(e) => this.showReportMenu(e)}>
                                {ellipsis}
                            </i>
                            {this.state.reportMenuVisible &&
                            <ReportMenu
                                visible={this.state.reportMenuVisible}
                                onReport={this.reportHandler.bind(this)}
                                onHide={() => this.setState({reportMenuVisible: false})}
                                focusLockStyle="cwt-report-profile-comments"
                                popupConnector={this.props.popupConnector}
                                usersConnector={this.props.usersConnector}
                                responsiveClass={this.props.responsiveClass}
                                renderInPopover={true}
                                mainTelemetryObject={this.telemetryObjects && this.telemetryObjects.commentItem}
                                {...{t, managedClasses, id, theme, me}}
                            />
                            }
                        </React.Fragment>
                    )
                        }
                    </div>
                </div>

                { innerContent }
            </li>
        );
    }
}